import { ServerDetails } from "@gymflow/api";
import { authenticationHelper } from "@gymflow/helpers";
import { UserMemberProfileDTO, UserStaffProfileDTO } from "@gymflow/types";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { DateTime } from "luxon";
import packageJson from "package.json";
import { usePostHog } from "posthog-js/react";
import qs from "qs";
import { useCallback, useEffect } from "react";

import { rememberMeRouteIdKey } from "./RememberMeRouteIdKey";

let refreshPromise:
  | Promise<{ accessToken: string; refreshToken: string }>
  | undefined;

async function getNewTokens(apiMeta: ServerDetails, refreshToken: string) {
  const response = await axios.post(
    `${apiMeta.urls.auth}/realms/${apiMeta.authRealm}/protocol/openid-connect/token`,
    qs.stringify({
      client_id: "gymflow-app",
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }),
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
  return {
    accessToken: response.data.access_token as string,
    refreshToken: response.data.refresh_token as string,
  };
}

async function getTokens(
  apiMeta: ServerDetails,
  { username, password }: { username: string; password: string },
) {
  const response = await axios.post(
    `${apiMeta.urls.auth}/realms/${apiMeta.authRealm}/protocol/openid-connect/token`,
    qs.stringify({
      username,
      password,
      client_id: "gymflow-app",
      grant_type: "password",
      scope: "profile offline_access",
    }),
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
  return {
    accessToken: response.data.access_token as string,
    refreshToken: response.data.refresh_token as string,
  };
}

const migrateLocalStorage = (
  userType: "staff" | "member",
  serverDetails?: ServerDetails,
) => {
  const oldInstanceKey = `${serverDetails?.urls.api} ${serverDetails?.authRealm}`;
  const oldRefreshToken = localStorage.getItem(
    `refresh-token ${oldInstanceKey}`,
  );
  const oldToken = localStorage.getItem(`access-token ${oldInstanceKey}`);
  if (!oldToken || !oldRefreshToken) {
    localStorage.removeItem(`access-token ${oldInstanceKey}`);
    localStorage.removeItem(`refresh-token ${oldInstanceKey}`);
    return;
  }
  const oldTokenParsed = authenticationHelper.parseJwt(oldToken);
  const oldTokenUserType = oldTokenParsed.realm_access.roles.includes("MEMBER")
    ? "member"
    : "staff";
  if (oldTokenUserType !== userType) {
    localStorage.removeItem(`access-token ${oldInstanceKey}`);
    localStorage.removeItem(`refresh-token ${oldInstanceKey}`);
    return;
  }

  const staffNewInstanceKey = `v2 ${userType} ${serverDetails?.authRealm}`;
  const memberNewInstanceKey = `v2 ${userType} ${serverDetails?.clubId} ${serverDetails?.authRealm}`;
  const newInstanceKey =
    userType === "staff" ? staffNewInstanceKey : memberNewInstanceKey;
  localStorage.setItem(`access-token ${newInstanceKey}`, oldToken);
  localStorage.setItem(`refresh-token ${newInstanceKey}`, oldRefreshToken);
  localStorage.removeItem(`access-token ${oldInstanceKey}`);
  localStorage.removeItem(`refresh-token ${oldInstanceKey}`);
};

export const useAuthentication = ({
  api,
  serverDetails,
  userType,
}: {
  api: AxiosInstance;
  serverDetails?: ServerDetails;
  userType: "staff" | "member";
}) => {
  migrateLocalStorage(userType, serverDetails);
  const posthog = usePostHog();
  // Members have a session per club, staff have a session per brand
  const instanceKeyStaff = `v2 ${userType} ${serverDetails?.authRealm}`;
  const instanceKeyMember = `v2 ${userType} ${serverDetails?.clubId} ${serverDetails?.authRealm}`;
  const instanceKey =
    userType === "staff" ? instanceKeyStaff : instanceKeyMember;
  const accessTokenKey = `access-token ${instanceKey}`;
  const refreshTokenKey = `refresh-token ${instanceKey}`;

  const {
    data: userToken,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["userToken", instanceKey],
    queryFn: async () => {
      const accessToken = localStorage.getItem(accessTokenKey);
      api.interceptors.request.clear();
      if (!accessToken) {
        delete api.defaults.headers.common["Authorization"];
        return { isLoggedIn: false };
      }
      api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      api.interceptors.request.use(async (config) => {
        const parsedAccessToken = authenticationHelper.parseJwt(accessToken);
        const expiry = DateTime.fromSeconds(parsedAccessToken.exp).minus({
          seconds: 5,
        });
        if (expiry < DateTime.now()) {
          try {
            const refreshToken = localStorage.getItem(
              `refresh-token ${instanceKey}`,
            );
            if (!refreshToken) {
              throw new Error("refresh token not found");
            }

            if (refreshPromise) {
              await refreshPromise;
            } else {
              refreshPromise = getNewTokens(serverDetails!, refreshToken);
              try {
                const newTokens = await refreshPromise;
                localStorage.setItem(accessTokenKey, newTokens.accessToken);
                localStorage.setItem(refreshTokenKey, newTokens.refreshToken);
                refetch();
              } finally {
                refreshPromise = undefined;
              }
            }

            const currentAccessToken = localStorage.getItem(accessTokenKey);
            config.headers["Authorization"] = `Bearer ${currentAccessToken}`;
            return config;
          } catch (error) {
            if (isAxiosError(error)) {
              if (error.response?.status === HttpStatusCode.BadRequest) {
                logout();
              }
            }
          }
        }
        config.headers["Authorization"] = `Bearer ${accessToken}`;
        return config;
      });
      const parsedAccessToken = authenticationHelper.parseJwt(accessToken);
      Sentry.setUser({
        id: parsedAccessToken.sub,
        email: parsedAccessToken.email,
      });
      posthog?.identify(parsedAccessToken.sub);
      posthog?.group("region", serverDetails?.serverId ?? "");
      posthog?.group("clubId", serverDetails?.clubId.toString() ?? "");
      if (userType === "staff") {
        (window as any)?.dataLayer?.push({
          event: "GymflowSettingsReady",
          gymflowIsStaff: true,
          gymflowUserId: parsedAccessToken.sub,
          gymflowHostname: window.location.hostname,
          gymflowUserName: "",
          gymflowUserEmail: parsedAccessToken.email,
        });
      }
      return {
        isLoggedIn: true,
        parsedAccessToken,
        accessToken,
      };
    },
    enabled: !!serverDetails,
  });
  const logout = useCallback(async () => {
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);
    localStorage.removeItem(rememberMeRouteIdKey);
    Sentry.setUser(null);
    await refetch();
  }, [accessTokenKey, refetch, refreshTokenKey]);

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const tokens = await getTokens(serverDetails!, {
          username: username.trim(),
          password,
        });
        const currentUser = await axios.get<
          UserStaffProfileDTO | UserMemberProfileDTO
        >(
          userType === "staff"
            ? `${serverDetails!.urls.api}/club/${serverDetails!.clubId}/user-staff-profile/current`
            : `${serverDetails!.urls.api}/club/${serverDetails!.clubId}/customer/user-member-profile`,
          {
            headers: {
              "x-data-version": packageJson.version,
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          },
        );
        console.info(
          `Logged in as ${
            "userStaffId" in currentUser.data
              ? currentUser.data.userStaffId
              : currentUser.data.userMemberId
          }`,
        );

        localStorage.setItem(accessTokenKey, tokens.accessToken);
        localStorage.setItem(refreshTokenKey, tokens.refreshToken);
        await refetch();
        return {
          success: true,
        } as const;
      } catch (error) {
        if (isAxiosError(error)) {
          if (
            error.response?.status === HttpStatusCode.Unauthorized ||
            error.response?.status === HttpStatusCode.Forbidden
          ) {
            return {
              success: false,
              error: "WRONG_CREDENTIALS",
            } as const;
          }
        }
        throw error;
      }
    },
    [accessTokenKey, refetch, refreshTokenKey, serverDetails, userType],
  );

  useEffect(() => {
    if (
      userType === "staff" &&
      !isLoading &&
      !userToken?.isLoggedIn &&
      window.location.pathname !== "/"
    ) {
      localStorage.removeItem(rememberMeRouteIdKey);
      // eslint-disable-next-line react-compiler/react-compiler
      window.location.href = "/";
    }
  }, [isLoading, userToken?.isLoggedIn, userType]);

  return {
    login,
    logout,
    userId: userToken?.parsedAccessToken?.sub,
    isAuthenticated: userToken?.isLoggedIn,
    isLoading,
    userType,
  };
};
